<template>
  <div class="labeler-main">
    <div class="container-fluid h-100">
      <div class="row h-100">
        <!-- Left Panel - Image and Canvas -->
        <div class="col-md-8 left-panel">
          <div class="card h-100">
            <div class="card-header bg-primary text-white">
              <h5 class="mb-0">
                <font-awesome-icon icon="image" class="me-2" />
                Image Annotation:
                <small v-if="currentImage" class="ms-2 text-muted-light">{{
                  currentImage.image?.name
                }}</small>
              </h5>
            </div>
            <div class="card-body p-0 position-relative">
              <!-- Loading Spinner -->
              <div
                v-if="isLoadingImage || !currentImage"
                class="loading-overlay"
              >
                <div class="spinner-border text-warning" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <p class="loading-text mt-3">Loading Image...</p>
              </div>

              <div v-else class="image-container">
                <!-- OHIF Viewer for version 2 projects with DICOM data -->
                <div
                  v-if="shouldUseOhifViewer && !socketConnected"
                  class="loading-overlay"
                >
                  <div class="spinner-border text-warning" role="status">
                    <span class="visually-hidden">Connecting to viewer...</span>
                  </div>
                  <p class="loading-text mt-3">Initializing Viewer...</p>
                </div>
                <OhifViewerIframe
                  v-else-if="shouldUseOhifViewer && socketConnected"
                  :src="viewerUrl"
                />
                <!-- Canvas for version 1 projects or images without DICOM data -->
                <AnnotationCanvas
                  v-else
                  :image-url="imageUrl"
                  :image-id="currentImageId || 'test-image'"
                  :image-filter="imageFilter"
                  :current-image="currentImage"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Right Panel - Controls and Annotations -->
        <div class="col-md-4 right-panel">
          <div class="card h-100">
            <div
              class="card-header bg-secondary text-white d-flex align-items-center justify-content-between"
            >
              <h5 class="mb-0">
                <font-awesome-icon icon="tools" class="me-2" />
                Report Summary
              </h5>
              <button
                class="dashboard-close-btn"
                @click="close()"
              >
                <font-awesome-icon size="lg" icon="times" />
              </button>
            </div>
            <div class="card-body overflow-auto">
              <!-- Project Info -->
              <div v-if="project" class="mb-3 p-3 project-info rounded">
                <h6 class="mb-1 color-white">{{ project.name }}</h6>
                <small class="text-muted">{{ project.description }}</small>
              </div>

              <!-- Image Set FilmStrip (for version 1 projects with image sets) -->
              <ImageSetFilmStrip v-if="shouldShowFilmStrip" />

              <!-- Drawing Mode Toggle (hidden for version 2 projects) -->
              <DrawingModeToggle v-if="!shouldUseOhifViewer" />

              <!-- Annotations List -->
              <AnnotationList :project="project" />

              <!-- Workflow Actions -->
              <WorkflowActions
                @save-and-next="handleSaveAndNext"
                @save-as-normal="handleSaveAsNormal"
                @ai-segment="handleAISegment"
                @reject="handleReject"
                class="mt-3"
              />

              <!-- Image Enhancement Controls (only for version 1 projects) -->
              <div
                v-if="project?.version === 1"
                class="enhance-form px-3 pt-3 pb-3 mt-3 rounded"
              >
                <h6 class="color-white mb-3">
                  <font-awesome-icon icon="adjust" class="me-2" />
                  Enhance Image
                </h6>

                <!-- Brightness -->
                <div class="mb-2">
                  <div class="d-flex align-items-center">
                    <div class="enhancement-icon">
                      <font-awesome-icon
                        icon="sun"
                        class="color-yellow"
                        title="Brightness"
                      />
                    </div>
                    <input
                      v-model="brightness"
                      type="range"
                      min="0"
                      max="200"
                      class="form-range flex-grow-1"
                    />
                  </div>
                </div>

                <!-- Contrast -->
                <div class="mb-2">
                  <div class="d-flex align-items-center">
                    <div class="enhancement-icon">
                      <font-awesome-icon
                        icon="adjust"
                        class="color-yellow"
                        title="Contrast"
                      />
                    </div>
                    <input
                      v-model="contrast"
                      type="range"
                      min="0"
                      max="200"
                      class="form-range flex-grow-1"
                    />
                  </div>
                </div>

                <!-- Saturate -->
                <div class="mb-2">
                  <div class="d-flex align-items-center">
                    <div class="enhancement-icon">
                      <font-awesome-icon
                        icon="tint"
                        class="color-yellow"
                        title="Saturation"
                      />
                    </div>
                    <input
                      v-model="saturate"
                      type="range"
                      min="0"
                      max="200"
                      class="form-range flex-grow-1"
                    />
                  </div>
                </div>

                <!-- Sepia -->
                <div class="mb-2">
                  <div class="d-flex align-items-center">
                    <div class="enhancement-icon">
                      <font-awesome-icon
                        icon="camera"
                        class="color-yellow"
                        title="Sepia"
                      />
                    </div>
                    <input
                      v-model="sepia"
                      type="range"
                      min="0"
                      max="100"
                      class="form-range flex-grow-1"
                    />
                  </div>
                </div>

                <!-- Invert -->
                <div class="mb-0">
                  <div class="d-flex align-items-center">
                    <div class="enhancement-icon">
                      <font-awesome-icon
                        icon="fill"
                        class="color-yellow"
                        title="Invert"
                      />
                    </div>
                    <input
                      v-model="invert"
                      type="range"
                      min="0"
                      max="100"
                      class="form-range flex-grow-1"
                    />
                  </div>
                </div>
              </div>

              <!-- Error Display -->
              <BAlert
                v-if="error"
                variant="danger"
                dismissible
                @dismissed="clearError"
                class="mt-3"
              >
                {{ error }}
              </BAlert>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Unsaved Changes Warning Modal -->
    <BModal
      v-model="showUnsavedWarning"
      title="Unsaved Changes"
      header-class="modal-header-custom"
      title-class="text-white h4"
      ok-title="Leave"
      cancel-title="Stay"
      @ok="confirmLeave"
    >
      <p>You have unsaved annotations. Are you sure you want to leave?</p>
    </BModal>

    <!-- No Images Modal -->
    <BModal
      v-model="showNoImagesModal"
      title="All Images Labeled"
      title-class="text-white h4"
      ok-only
      ok-title="Back to Projects"
      @ok="goToProjects"
    >
      <p>Great job! All images have been labeled.</p>
      <p class="mb-0">
        There are no more images to label in this Project.
      </p>
    </BModal>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  getCurrentInstance,
} from "vue";
import { useStore } from "vuex";
import AnnotationCanvas from "./components/AnnotationCanvas.vue";
import DrawingModeToggle from "./components/DrawingModeToggle.vue";
import AnnotationList from "./components/AnnotationList.vue";
import WorkflowActions from "./components/WorkflowActions.vue";
import OhifViewerIframe from "./components/OhifViewerIframe.vue";
import ImageSetFilmStrip from "./components/ImageSetFilmStrip.vue";
import { apiRoute, viewerRoute } from "@/helpers/api-route";
import { labelledImagesService } from "@/services/labels.services";

const store = useStore();
const instance = getCurrentInstance();

const showUnsavedWarning = ref(false);
const showNoImagesModal = ref(false);
const pendingNavigation = ref(null);
const socketConnected = ref(false);
const receivedAnnotations = ref(null);
const projectPollingInterval = ref(null);

// Image enhancement values
const brightness = ref(100);
const contrast = ref(100);
const saturate = ref(100);
const sepia = ref(0);
const invert = ref(0);
const hue = ref(0);

// Get projectId and imageId from workspace store instead of route params
const projectId = computed(() => store.state.workspacePanel.labelerProjectId);
const imageId = computed(() => store.state.workspacePanel.labelerImageId);
const project = computed(() => {
  const projects = store.state.projectSubmission?.projects;
  if (projects && projectId.value) {
    return projects.find((p) => p._id === projectId.value);
  }
  return null;
});
const currentImage = computed(() => store.state.labelerImageQueue.currentImage);
const currentImageId = computed(
  () => store.getters["labelerImageQueue/currentImageId"]
);
const isLoadingImage = computed(() => store.state.labelerImageQueue.isLoading);
const hasUnsavedChanges = computed(
  () => store.state.labelerAnnotation.hasUnsavedChanges
);
const error = computed(
  () =>
    store.state.labelerAnnotation.error || store.state.labelerImageQueue.error
);
const unlabeledCount = computed(
  () => store.state.labelerImageQueue.unlabeledCount
);
const user = computed(() => store.state.auth?.user);

const imageUrl = computed(() => {
  if (!currentImage.value) {
    // Return a data URL placeholder image for development/testing
    return "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAwIiBoZWlnaHQ9IjYwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iODAwIiBoZWlnaHQ9IjYwMCIgZmlsbD0iIzBjMjAzMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjQiIGZpbGw9IiNmZmZmZmYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5UZXN0IEltYWdlIC0gRHJhdyBIZXJlPC90ZXh0Pjwvc3ZnPg==";
  }
  return `${apiRoute()}/image/${currentImage.value._id}`;
});

// Image filter string for CSS
const imageFilter = computed(() => {
  return `brightness(${brightness.value}%) contrast(${contrast.value}%) saturate(${saturate.value}%) sepia(${sepia.value}%) hue-rotate(${hue.value}deg) invert(${invert.value}%)`;
});

// Check if we should use OHIF viewer (version 2 with DICOM data)
const shouldUseOhifViewer = computed(() => {
  return (
    project.value?.version === 2 && currentImage.value?.dicom?.StudyInstanceUID
  );
});

// Check if we should show FilmStrip (version 1 projects with image sets)
const shouldShowFilmStrip = computed(() => {
  const version = project.value?.version;
  const hasImageSetName = currentImage.value?.image_set?.name;
  const hasImageSet = store.getters["labelerImageQueue/hasImageSet"];
  const imageSetLength = store.state.labelerImageQueue.imageSet.length;

  console.log("FilmStrip visibility check:", {
    version,
    hasImageSetName,
    hasImageSet,
    imageSetLength,
    currentImage: currentImage.value?._id,
  });

  return version === 1 && hasImageSetName && hasImageSet;
});

// OHIF viewer URL
const viewerUrl = computed(() => {
  if (!shouldUseOhifViewer.value) return "";

  const socketio = instance?.appContext.config.globalProperties.$socketio;
  const hash = socketio?.hash();
  const dicom = currentImage.value.dicom;
  const viewerBaseUrl = viewerRoute() + "/viewer";

  console.log("Generating OHIF viewer URL:", {
    socketio: !!socketio,
    hash: hash,
    dicom: dicom,
    StudyInstanceUID: dicom?.StudyInstanceUID,
  });

  if (!hash) {
    console.warn(
      "Socket hash not available yet. Socket may not be initialized."
    );
  }

  // Build params based on project grouping
  let params = `?StudyInstanceUIDs=${dicom.StudyInstanceUID}&hash=${hash}`;

  // Add SeriesInstanceUID if project grouping is set to "series"
  if (project.value?.extra?.grouping === "series" && dicom.SeriesInstanceUID) {
    params += `&SeriesInstanceUIDs=${dicom.SeriesInstanceUID}`;
  }

  const fullUrl = viewerBaseUrl + params;
  console.log("Generated OHIF viewer URL:", fullUrl);

  return fullUrl;
});

// Socket connection initialization
const initializeSocketConnection = async () => {
  const socketio = instance?.appContext.config.globalProperties.$socketio;

  console.log("Initializing socket connection...", {
    socketioAvailable: !!socketio,
    userAvailable: !!user.value,
    hashMethod: !!socketio?.hash,
  });

  if (!socketio || !user.value) {
    console.error("Socket or user not available", {
      socketio: !!socketio,
      user: !!user.value,
    });
    return;
  }

  // Check if hash is available before connection
  const preConnectionHash = socketio.hash();
  console.log("Pre-connection hash:", preConnectionHash);

  // Setup connection if not already connected
  if (!socketio.connected()) {
    try {
      const status = await socketio.setupConnection(user.value.email);
      socketConnected.value = status;
      console.log("Socket connected:", status);

      // Check hash after connection
      const postConnectionHash = socketio.hash();
      console.log("Post-connection hash:", postConnectionHash);
    } catch (error) {
      console.error("Socket connection failed:", error);
      socketConnected.value = false;
      return;
    }
  } else {
    socketConnected.value = true;
    console.log("Socket already connected");
  }

  // Send JWT and select project
  if (socketConnected.value) {
    socketio.sendJwt(user.value.token);
    socketio.selectProject(projectId.value);
    console.log("JWT sent and project selected:", projectId.value);

    // Register broadcast receiver for real-time annotations
    socketio.registerBroadcastReceiver(handleReceivedBroadcast);

    // Setup polling to re-select project every 30 seconds
    projectPollingInterval.value = setInterval(() => {
      if (projectId.value && socketio.connected()) {
        socketio.selectProject(projectId.value);
      }
    }, 30000);
  }
};

// Handle received broadcasts from other clients
const handleReceivedBroadcast = (data) => {
  console.log("Received broadcast: Full data", data);

  // Handle only 'Pathologies' type (Prospective is for retros only)
  if (data?.type === "Pathologies" && currentImage.value?.dicom) {
    const currentDicom = currentImage.value.dicom;
    const studyUID = currentDicom.StudyInstanceUID;
    const seriesUID = currentDicom.SeriesInstanceUID;
    const sopUID = currentDicom.SOPInstanceUID;

    console.log("Received broadcast: Current image DICOM", {
      StudyInstanceUID: studyUID,
      SeriesInstanceUID: seriesUID,
      SOPInstanceUID: sopUID,
    });

    // Collect ALL pathologies from ALL matching items first
    const allPathologies = [];

    data.value?.forEach((item) => {
      let isMatch = false;

      // Check for LabelerMain format (StudyInstanceUIDs)
      if (item.StudyInstanceUIDs) {
        console.log(
          "Received broadcast: Checking LabelerMain format item",
          item.StudyInstanceUIDs,
          "vs",
          studyUID
        );
        isMatch = item.StudyInstanceUIDs === studyUID;
      }
      // Check for DataAnnotations format (SeriesInstanceUID + SOPInstanceUID)
      else if (item.SeriesInstanceUID && item.SOPInstanceUID) {
        console.log(
          "Received broadcast: Checking DataAnnotations format item",
          {
            SeriesInstanceUID: item.SeriesInstanceUID,
            SOPInstanceUID: item.SOPInstanceUID,
          },
          "vs current",
          { seriesUID, sopUID }
        );
        isMatch =
          item.SeriesInstanceUID === seriesUID &&
          item.SOPInstanceUID === sopUID;
      }

      if (isMatch && item?.pathologies) {
        console.log(
          "Received broadcast: Match found! Item has",
          item.pathologies.length,
          "pathologies"
        );
        allPathologies.push(...item.pathologies);
      }
    });

    if (allPathologies.length > 0) {
      console.log(
        "Received broadcast: Completely replacing all annotations with",
        allPathologies.length,
        "total pathologies from all items"
      );
      receivedAnnotations.value = allPathologies;

      // Clear ALL existing annotations - broadcast completely replaces everything
      console.log(
        "Received broadcast: Clearing all",
        store.state.labelerAnnotation.annotations.length,
        "existing annotations"
      );
      store.commit("labelerAnnotation/SET_ANNOTATIONS", []);

      // Add new received pathologies (which will be the ONLY annotations)
      allPathologies.forEach((pathology, index) => {
        // Extract freehand points from various formats
        let freehandpoints = pathology.freehandpoints || [];
        let coords = pathology.coords;

        // Check if points are in planes.yz format (from OHIF)
        if (!freehandpoints.length && pathology.planes?.yz) {
          freehandpoints = pathology.planes.yz.map((point) => ({
            x: point[0],
            y: point[1],
          }));
          console.log(
            "Received broadcast: Converted",
            freehandpoints.length,
            "points from planes.yz for pathology",
            pathology.name
          );
        }

        // Check if points are in planes.xy format
        if (!freehandpoints.length && pathology.planes?.xy) {
          freehandpoints = pathology.planes.xy.map((point) => ({
            x: point[0],
            y: point[1],
          }));
          console.log(
            "Received broadcast: Converted",
            freehandpoints.length,
            "points from planes.xy for pathology",
            pathology.name
          );
        }

        // Check if points are from DataAnnotations format (simple points array)
        if (
          !freehandpoints.length &&
          pathology.points &&
          pathology.points.length > 0
        ) {
          // If exactly 4 points, it's a bounding box - convert to coords
          if (pathology.points.length === 4) {
            const xs = pathology.points.map((p) => p.x);
            const ys = pathology.points.map((p) => p.y);
            coords = {
              x: Math.min(...xs),
              y: Math.min(...ys),
              w: Math.max(...xs) - Math.min(...xs),
              h: Math.max(...ys) - Math.min(...ys),
            };
            console.log(
              "Received broadcast: Converted 4 points to bounding box coords for pathology",
              pathology.name
            );
          } else {
            // More than 4 points, treat as freehand
            freehandpoints = pathology.points.map((p) => ({ x: p.x, y: p.y }));
            console.log(
              "Received broadcast: Converted",
              freehandpoints.length,
              "points from DataAnnotations format for pathology",
              pathology.name
            );
          }
        }

        const annotation = {
          id: crypto.randomUUID(),
          type:
            freehandpoints && freehandpoints.length > 0 ? "freehand" : "box",
          coords: coords,
          freehandpoints: freehandpoints,
          pathology: pathology.name,
          color: pathology.color || "#ed1a4f",
          isReceived: true, // Mark as received from broadcast
          instanceId: pathology.instanceId || sopUID,
          mapped_stats: pathology.mapped_stats || {},
          other: pathology.other || [], // Include 'other' measurements field
        };

        console.log(
          "Received broadcast: Adding annotation",
          index + 1,
          "/",
          allPathologies.length,
          "-",
          annotation.pathology
        );
        store.commit("labelerAnnotation/ADD_ANNOTATION", annotation);
      });

      console.log(
        "Received broadcast: Total annotations now:",
        store.state.labelerAnnotation.annotations.length
      );
    } else {
      console.log(
        "Received broadcast: No matching items found for current image DICOM IDs"
      );
    }
  } else {
    console.log(
      "Received broadcast: Type mismatch or no current image. Type:",
      data?.type,
      "Has image:",
      !!currentImage.value
    );
  }
};

// Process and format pathologies for broadcasting
const processPathologiesForBroadcast = () => {
  if (!currentImage.value?.dicom?.StudyInstanceUID) return [];

  return [
    {
      StudyInstanceUIDs: currentImage.value.dicom.StudyInstanceUID,
      pathologies: store.state.labelerAnnotation.annotations.map((ann) => ({
        name: ann.pathology,
        color: ann.color,
        coords: ann.coords,
        freehandpoints: ann.freehandpoints || [],
        instanceId: ann.instanceId || currentImage.value.dicom.SOPInstanceUID,
        mapped_stats: {},
        planes: {},
        z: null,
        other: [],
        can3dSegment: false,
        window: null,
        autoSegment: [],
      })),
    },
  ];
};

onMounted(async () => {
  // Initialize the labeler
  await initializeLabeler();

  // Setup socket connection for version 2 projects
  if (shouldUseOhifViewer.value) {
    await initializeSocketConnection();

    // Broadcast current saved annotations to OHIF after connection
    // Wait 5 seconds after project data is sent for the first time
    if (socketConnected.value) {
      setTimeout(() => {
        const socketio = instance?.appContext.config.globalProperties.$socketio;
        const broadcastData = processPathologiesForBroadcast();

        if (
          broadcastData.length > 0 &&
          broadcastData[0].pathologies.length > 0
        ) {
          socketio.broadcast({ type: "Pathologies", value: broadcastData });
          console.log("Broadcasting saved annotations on load:", broadcastData);
        }
      }, 5000);
    }
  }
});

const initializeLabeler = async () => {
  if (!projectId.value) {
    console.error("No project ID provided");
    return;
  }

  // Load all user projects
  await store.dispatch("projectSubmission/getUserProjects");

  // Select the current project
  store.dispatch("projectSubmission/selectProject", projectId.value);

  // Fetch project config
  await store.dispatch("labelerAnnotation/fetchProjectConfig", projectId.value);

  // Set project ID in the queue
  store.dispatch("labelerImageQueue/setProjectId", projectId.value);

  // Fetch image count
  await store.dispatch("labelerImageQueue/fetchImageCount");

  // Check if we're loading a specific image or the next unlabeled one
  if (imageId.value) {
    console.log("Loading specific image for editing:", imageId.value);
    const loadedImage = await store.dispatch(
      "labelerImageQueue/fetchSpecificImage",
      imageId.value
    );

    // If failover occurred (loaded image doesn't match requested imageId), update store
    if (loadedImage && loadedImage._id !== imageId.value) {
      console.log("Failover occurred - clearing specific imageId from store");
      store.commit("workspacePanel/setLabelerMode", {
        active: true,
        projectId: projectId.value,
        imageId: null, // Clear specific imageId, will use next unlabeled
        previousView: store.state.workspacePanel.labelerPreviousView,
      });
    }
  } else {
    console.log("Loading next unlabeled image");
    await store.dispatch("labelerImageQueue/fetchNextImage");

    // Check if there are images to label
    if (unlabeledCount.value === 0) {
      showNoImagesModal.value = true;
    }
  }
};

const handleSaveAndNext = async () => {
  if (!currentImageId.value) return;

  // First, save current image's annotations to the temporary storage
  await store.dispatch("labelerImageQueue/saveCurrentImageSetAnnotations");

  // Check if this is an image set (version 1 with multiple images)
  const imageSet = store.state.labelerImageQueue.imageSet;
  const imageSetAnnotations = store.state.labelerImageQueue.imageSetAnnotations;
  const hasImageSet = imageSet.length > 1 && project.value?.version === 1;

  if (hasImageSet) {
    // Batch all annotations from all images into a SINGLE POST request
    // This prevents race conditions when backend fills in blank labels
    console.log("Batching all image set annotations into single request...");

    // Use the first image (main/representative) as the target
    const mainImage = imageSet[0];
    const mainImageId = mainImage._id;

    // Diagnoses/Tags/Comments are STUDY-LEVEL (same for all images)
    // Use current labelerAnnotation state
    const currentDiagnoses = [...store.state.labelerAnnotation.selectedDiagnoses];
    const currentTags = [...store.state.labelerAnnotation.selectedTags];
    const currentComments = store.state.labelerAnnotation.comments || "";
    // Pathologies are IMAGE-LEVEL - include ALL current annotations (DB + user-created)
    const currentAnnotations = [...store.state.labelerAnnotation.annotations];
    const currentViewingImageId = currentImageId.value;

    // Collect pathologies from ALL images in the set
    // For the CURRENT image: use live labelerAnnotation state (what user sees on screen)
    // For OTHER images: use imageSetAnnotations (saved when navigating, or loaded from DB)
    const allPathologies = [];

    for (let i = 0; i < imageSet.length; i++) {
      const image = imageSet[i];
      const imageId = image._id;
      const isCurrentImage = imageId === currentViewingImageId;

      // Use live state for current image, saved state for others
      // Include ALL annotations (DB + user-created) - this is the current state
      const annotations = isCurrentImage
        ? currentAnnotations
        : (imageSetAnnotations[imageId]?.annotations || []);

      // Skip images with no pathologies - don't include empty entries
      if (annotations.length === 0) {
        continue;
      }

      // Add all annotations with instanceId to identify which image they belong to
      // Backend uses instanceId (SOPInstanceUID) when z <= 0 to find the correct image
      const imageInstanceId = image.dicom?.SOPInstanceUID || null;

      annotations.forEach((ann) => {
        allPathologies.push({
          coords: ann.coords || undefined,
          name: ann.pathology,
          color: ann.color,
          freehandpoints: ann.freehandpoints || [],
          planes: ann.planes || {},
          // Set z to 0 to trigger instanceId-based lookup in backend
          // (backend uses instanceId when z <= 0)
          z: 0,
          other: ann.other || [],
          can3dSegment: ann.can3dSegment || false,
          window: ann.window || image.image_set?.window || null,
          mapped_stats: ann.mapped_stats || {},
          autoSegment: ann.autoSegment || [],
          // instanceId (SOPInstanceUID) uniquely identifies the image
          instanceId: imageInstanceId || ann.instanceId || null,
        });
      });
    }

    console.log(`Collected ${allPathologies.length} pathologies from ${imageSet.length} images`);

    // Send single POST request with all pathologies to the main image
    // Use current diagnoses/tags/comments from the active labelerAnnotation state
    const labelData = {
      image: mainImageId,
      diagnosis: currentDiagnoses[0] || "",
      diagnoses: currentDiagnoses,
      tags: currentTags,
      pathologies: allPathologies,
      comments: currentComments,
      projectId: projectId.value,
    };

    try {
      const { annotationService } = await import(
        "@/services/annotation.service"
      );
      await annotationService.saveLabel(mainImageId, labelData);
      console.log("Successfully saved all image set annotations in single request");
    } catch (error) {
      console.error("Failed to save image set annotations:", error);
      store.commit(
        "labelerAnnotation/SET_ERROR",
        "Failed to save image set annotations"
      );
      return;
    }
  } else {
    // Single image save (original logic)
    const success = await store.dispatch("labelerAnnotation/saveAnnotations", {
      imageId: currentImageId.value,
      projectId: projectId.value,
    });

    if (!success) return;
  }

  await store.dispatch("labelerImageQueue/fetchImageCount");

  // Broadcast annotations for version 2 projects (OHIF viewer)
  if (shouldUseOhifViewer.value && socketConnected.value) {
    const socketio = instance?.appContext.config.globalProperties.$socketio;
    const broadcastData = processPathologiesForBroadcast();

    if (broadcastData.length > 0) {
      socketio.broadcast({ type: "Prospective", value: broadcastData });
      console.log("Broadcasting saved annotations:", broadcastData);
    }
  }

  // Save current image state to previous images before moving to next
  if (currentImage.value) {
    store.commit("labelerImageQueue/ADD_PREVIOUS_IMAGE", {
      image: currentImage.value,
      annotations: [...store.state.labelerAnnotation.annotations],
      diagnoses: [...store.state.labelerAnnotation.selectedDiagnoses],
      tags: [...store.state.labelerAnnotation.selectedTags],
      comments: store.state.labelerAnnotation.comments,
      pathologies: store.state.labelerAnnotation.annotations.map((ann) => ({
        name: ann.pathology,
        color: ann.color,
        coords: ann.coords,
        freehandpoints: ann.freehandpoints,
      })),
    });
  }

  // Clear the current state and image set annotations
  store.dispatch("labelerAnnotation/clearAnnotations");
  store.commit("labelerImageQueue/CLEAR_IMAGE_SET_ANNOTATIONS");

  // If we were editing a specific image, clear it from store (will load next unlabeled)
  if (imageId.value) {
    store.commit("workspacePanel/setLabelerMode", {
      active: true,
      projectId: projectId.value,
      imageId: null, // Clear specific imageId
      previousView: store.state.workspacePanel.labelerPreviousView,
    });
  }

  const _savedImage = await labelledImagesService.getLabelledImagesSingle(
    projectId.value,
    currentImageId.value
  );
  await store.dispatch("workspacePanel/setUpdatedImage", {
    id: currentImageId.value,
    value: _savedImage,
    action: "label",
  });

  // Fetch next image
  const nextImage = await store.dispatch("labelerImageQueue/fetchNextImage");

  if (!nextImage && unlabeledCount.value === 0) {
    showNoImagesModal.value = true;
  }
};

const handleSaveAsNormal = async () => {
  // Clear annotations and set diagnosis to "Normal"
  store.commit("labelerAnnotation/CLEAR_ANNOTATIONS");
  store.commit("labelerAnnotation/SET_SELECTED_DIAGNOSES", ["normal"]);

  // Save and next
  await handleSaveAndNext();
};

const handleAISegment = async () => {
  if (!currentImageId.value) return;

  await store.dispatch("labelerAnnotation/fetchAILabels", {
    imageId: currentImageId.value,
    projectId: projectId.value,
  });

  // Broadcast AI-generated annotations for version 2 projects
  if (shouldUseOhifViewer.value && socketConnected.value) {
    const socketio = instance?.appContext.config.globalProperties.$socketio;
    const broadcastData = processPathologiesForBroadcast();

    if (broadcastData.length > 0) {
      socketio.aiBroadcastValues(broadcastData);
      console.log("Broadcasting AI annotations:", broadcastData);
    }
  }
};

const handleReject = async () => {
  // Clear the current state
  store.dispatch("labelerAnnotation/clearAnnotations");
  store.commit("labelerImageQueue/CLEAR_IMAGE_SET_ANNOTATIONS");

  // If we were editing a specific image, clear it from store
  if (imageId.value) {
    store.commit("workspacePanel/setLabelerMode", {
      active: true,
      projectId: projectId.value,
      imageId: null, // Clear specific imageId
      previousView: store.state.workspacePanel.labelerPreviousView,
    });
  }

  await store.dispatch("workspacePanel/setUpdatedImage", {
    id: currentImageId.value,
    value: null,
    action: "reject",
  });

  await store.dispatch("labelerImageQueue/fetchImageCount");

  // Fetch next image
  const nextImage = await store.dispatch("labelerImageQueue/fetchNextImage");

  if (!nextImage && unlabeledCount.value === 0) {
    showNoImagesModal.value = true;
  }
};

const clearError = () => {
  store.commit("labelerAnnotation/SET_ERROR", null);
  store.commit("labelerImageQueue/SET_ERROR", null);
};

const goToProjects = () => {
  // Exit labeler mode when no images modal is shown
  store.dispatch("workspacePanel/exitLabelerMode");
};

const close = () => {
  // Check for unsaved changes before exiting labeler mode
  if (hasUnsavedChanges.value) {
    showUnsavedWarning.value = true;
    pendingNavigation.value = () =>
      store.dispatch("workspacePanel/exitLabelerMode");
  } else {
    store.dispatch("workspacePanel/exitLabelerMode");
  }
};

const confirmLeave = () => {
  if (pendingNavigation.value) {
    pendingNavigation.value();
    pendingNavigation.value = null;
  }
};

// Cleanup on unmount
onBeforeUnmount(() => {
  store.dispatch("labelerAnnotation/clearAnnotations");

  // Clear polling interval
  if (projectPollingInterval.value) {
    clearInterval(projectPollingInterval.value);
    projectPollingInterval.value = null;
  }
});
</script>

<style scoped>
.labeler-main {
  height: 100%;
  min-height: calc(100vh - var(--header-height));
  padding: 1rem;
  background-color: var(--color-bg-secondary);
}

.left-panel,
.right-panel {
  padding: 0.5rem;
}

.card {
  background-color: var(--color-bg-secondary);
  border: var(--border-width-card) solid var(--color-border-secondary);
  border-radius: var(--border-radius-standard);
}

.card-header {
  background-color: var(--color-bg-secondary) !important;
  border-bottom: var(--border-width-card) solid var(--color-border-secondary);
}

.right-panel .card-header {
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
}

.card-header h5 {
  color: var(--color-text-primary) !important;
  font-weight: var(--font-weight-extrabold);
  letter-spacing: var(--letter-spacing-header);
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: var(--color-bg-primary);
  z-index: 20;
}

.loading-text {
  color: var(--color-accent-primary);
  font-size: 1.2rem;
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-header);
}

.spinner-border {
  width: 3rem;
  height: 3rem;
  border-width: 0.3rem;
}

.image-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-bg-black);
}

.image-container iframe {
  width: 100%;
  height: 100%;
}

.right-panel .card-body {
  max-height: calc(100vh - var(--header-height) - 100px);
  background-color: var(--color-bg-secondary);
}

.project-info {
  background-color: var(--color-bg-secondary);
  border: var(--border-width-card) solid var(--color-border-secondary);
  border-radius: var(--border-radius-small);
}

.project-info h6 {
  font-weight: var(--font-weight-extrabold);
  letter-spacing: var(--letter-spacing-header);
}

.text-muted {
  color: var(--color-text-muted) !important;
}

.text-muted-light {
  color: var(--color-text-secondary) !important;
  font-size: 0.85rem;
  font-weight: var(--font-weight-normal);
}

:deep(.modal-content) {
  background-color: var(--color-bg-secondary);
  color: var(--color-text-primary);
}

:deep(.modal-header) {
  background-color: var(--color-bg-primary);
  border-bottom: var(--border-width-standard) solid var(--color-border-primary);
}

:deep(.modal-title) {
  color: var(--color-text-primary);
  font-weight: var(--font-weight-extrabold);
}

:deep(.modal-body) {
  color: var(--color-text-primary);
}

:deep(.btn-primary) {
  background-color: var(--color-accent-primary);
  border-color: var(--color-accent-primary);
  color: var(--color-bg-primary);
  font-weight: var(--font-weight-extrabold);
}

:deep(.btn-secondary) {
  background-color: var(--color-button-secondary);
  border-color: var(--color-button-secondary);
  color: var(--color-text-primary);
  font-weight: var(--font-weight-extrabold);
}

:deep(.alert-danger) {
  background-color: var(--color-error);
  color: var(--color-text-primary);
  border-color: var(--color-error);
}

/* Image Enhancement Styles */
.enhance-form {
  background-color: var(--color-bg-secondary);
  border: var(--border-width-card) solid var(--color-border-secondary);
  border-radius: var(--border-radius-standard);
}

.enhance-form h6 {
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-header);
}

.enhancement-icon {
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
}

.color-yellow {
  color: var(--color-accent-primary);
}

.color-white {
  color: var(--color-text-primary);
}

/* Range slider styling */
.form-range {
  height: 6px;
  background-color: var(--color-bg-primary);
  border-radius: var(--border-radius-small);
}

.form-range::-webkit-slider-thumb {
  background-color: var(--color-accent-primary);
  border: none;
  width: 16px;
  height: 16px;
}

.form-range::-moz-range-thumb {
  background-color: var(--color-accent-primary);
  border: none;
  width: 16px;
  height: 16px;
}

.form-range::-webkit-slider-runnable-track {
  height: 5px;
  background-color: var(--color-bg-primary);
  border-radius: var(--border-radius-small);
}

.form-range::-moz-range-track {
  height: 5px;
  background-color: var(--color-bg-primary);
  border-radius: var(--border-radius-small);
}

/* Yellow button styling */
.btn-yellow {
  background-color: var(--color-accent-primary);
  border-color: var(--color-accent-primary);
  color: var(--color-bg-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: var(--font-weight-extrabold);
}

.btn-yellow:hover {
  background-color: var(--color-accent-hover);
  border-color: var(--color-accent-hover);
  color: #0c2030;
}

.color-navy {
  color: #0c2030;
}
</style>
